/**
 * Minutes late, measured from the session's SCHEDULED start (not when staff
 * actually pressed "start session"). A child arriving before the scheduled
 * start is on time — never negative, clamped at 0 — and after it, the whole
 * number of minutes elapsed.
 *
 * Deliberately timezone-aware via Intl rather than hand-rolled IST/GMT DST
 * math, matching the approach `dublinDateString` in `utils/date.ts` already
 * uses (and the mistake that approach exists to avoid repeating).
 */
import { dublinDateTimeMs } from './dublin-time';

/**
 * Minutes a child is late relative to the session's scheduled start.
 *
 * @param scheduledStart "HH:MM" — the session's scheduled start time.
 * @param sessionDate "YYYY-MM-DD" — the session's calendar date, Dublin.
 * @param at ISO datetime to measure from; defaults to now. A still-expected
 *   child should be measured against the current instant.
 * @returns `max(0, whole minutes(at − scheduledStartDateTime))`. Before the
 *   scheduled start (including exactly at it), returns 0.
 */
export function minutesLate(scheduledStart: string, sessionDate: string, at?: string): number {
  const atMs = at ? new Date(at).getTime() : Date.now();
  const scheduledMs = dublinDateTimeMs(sessionDate, scheduledStart);
  return Math.max(0, Math.floor((atMs - scheduledMs) / 60_000));
}
